import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { CopilotProcessingService } from '../../../_services/copilot-processing.service';
import { CopilotDocument } from '../../../_interfaces/document';
import { openCopilotSnackbar } from '../../../shared/components/copilot-snackbar/open-copilot-snackbar';

interface ReprocessJob {
  dictationId: string;
  templateId: number;
  templateType: string;
  action: string;
  status: string;
}

interface Batch {
  trackedKeys: Set<string>;
  completedKeys: Set<string>;
  failedKeys: Set<string>;
}

@Injectable({ providedIn: 'root' })
export class RegenerateBatchWatcherService {
  private readonly batches = new Map<string, Batch>();
  private readonly activeDictationIds = new Set<string>();
  private readonly multiFailureToastRefs = new Map<string, MatSnackBarRef<unknown>>();
  private readonly outstandingFailureKeys = new Map<string, Set<string>>();

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly translate: TranslateService,
    private readonly processing: CopilotProcessingService,
  ) {
    this.processing.processing$.subscribe(jobs => this.onJobsUpdate(jobs));
  }

  watch(dictationId: string, trackedDocs: CopilotDocument[]): void {
    if (!trackedDocs.length) return;

    this.batches.set(dictationId, {
      trackedKeys: new Set(trackedDocs.map(doc => this.keyForDoc(doc))),
      completedKeys: new Set<string>(),
      failedKeys: new Set<string>(),
    });
    this.activeDictationIds.add(dictationId);
  }

  isRegeneratingAll(dictationId: string | null | undefined): boolean {
    return !!dictationId && this.activeDictationIds.has(dictationId);
  }

  resolveFailure(dictationId: string, key: string): void {
    const remainingKeys = this.outstandingFailureKeys.get(dictationId);
    if (!remainingKeys) return;

    remainingKeys.delete(key);
    if (remainingKeys.size > 0) return;

    this.multiFailureToastRefs.get(dictationId)?.dismiss();
  }

  // ─── Reacting to processing job updates ────────────────────────────────

  private onJobsUpdate(jobs: ReprocessJob[]): void {
    for (const [dictationId, batch] of this.batches) {
      this.applyJobsToBatch(dictationId, batch, jobs);

      if (this.isBatchResolved(batch)) {
        this.finalizeBatch(dictationId, batch);
      }
    }
  }

  private applyJobsToBatch(dictationId: string, batch: Batch, jobs: ReprocessJob[]): void {
    const { trackedKeys, completedKeys, failedKeys } = batch;

    for (const job of jobs) {
      if (job.dictationId !== dictationId || job.action !== 'reprocess') continue;

      const key = this.keyFor(job.templateId, job.templateType);
      if (!trackedKeys.has(key)) continue;

      if (job.status === 'completed') completedKeys.add(key);
      else if (job.status === 'failed') failedKeys.add(key);
    }
  }

  private isBatchResolved(batch: Batch): boolean {
    const { trackedKeys, completedKeys, failedKeys } = batch;
    return [...trackedKeys].every(key => completedKeys.has(key) || failedKeys.has(key));
  }

  private finalizeBatch(dictationId: string, batch: Batch): void {
    this.batches.delete(dictationId);
    this.activeDictationIds.delete(dictationId);

    const { failedKeys } = batch;
    if (failedKeys.size === 0) {
      this.notifyAllRegenerated();
      return;
    }

    // Single failures are surfaced by the inline banner alone; only summarise
    // when several documents fail in the same run.
    if (failedKeys.size > 1) {
      this.notifyMultipleFailures(dictationId, failedKeys);
    }
  }

  // ─── Toast notifications ────────────────────────────────────────────────

  private notifyAllRegenerated(): void {
    openCopilotSnackbar(this.snackBar, this.translate.instant('All documents have been regenerated'));
  }

  private notifyMultipleFailures(dictationId: string, failedKeys: Set<string>): void {
    this.outstandingFailureKeys.set(dictationId, new Set(failedKeys));

    const toastRef = openCopilotSnackbar(
      this.snackBar,
      this.translate.instant('Failed to regenerate {{count}} documents', { count: failedKeys.size }),
    );
    this.multiFailureToastRefs.set(dictationId, toastRef);

    toastRef.afterDismissed().subscribe(() => this.forgetMultiFailureToast(dictationId, toastRef));
  }

  private forgetMultiFailureToast(dictationId: string, toastRef: MatSnackBarRef<unknown>): void {
    if (this.multiFailureToastRefs.get(dictationId) !== toastRef) return;

    this.multiFailureToastRefs.delete(dictationId);
    this.outstandingFailureKeys.delete(dictationId);
  }

  // ─── Key helpers ─────────────────────────────────────────────────────────

  private keyFor(templateId: number, templateType: string): string {
    return `${templateId}_${templateType}`;
  }

  private keyForDoc(doc: CopilotDocument): string {
    return this.keyFor(doc.templateId, doc.templateType);
  }
}
